import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { stripHtmlTags } from '../utils/renderHtml';

const MAX_DISPLAY_SYNONYMS = 10;

interface SynonymsCellProps {
  synonyms: string[];
  totalCount: number;
  onViewAll?: () => void;
}

/**
 * Dedicated synonyms grid cell (Seq 4.4, PIV-033) -- NOT the shared
 * ExpandableListCell, whose "+N more" is driven by the (already-capped)
 * array's own length and caps display at 3. The server caps `synonyms` at
 * 10 (attachMultiValueFields, api/sql/productsFlat.js) and reports the real
 * total separately via `synonym_total_count`, so the "View all" button's
 * visibility is driven by that server total, not by re-counting the array.
 */
const SynonymsCell: React.FC<SynonymsCellProps> = ({
  synonyms,
  totalCount,
  onViewAll,
}) => {
  if (!synonyms || synonyms.length === 0) {
    return <span style={emptyStyle}>-</span>;
  }

  const displayItems = synonyms
    .slice(0, MAX_DISPLAY_SYNONYMS)
    .map(stripHtmlTags);
  const hasMore = totalCount > displayItems.length;

  const handleViewAllClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Stops the grid's own row/cell click+focus handling from also firing --
    // same stopPropagation precedent as ExpandableCell.tsx's "+N more" span
    // and ProductsRowGroupingDataGrid's GroupRowCell expand toggle.
    event.stopPropagation();
    onViewAll?.();
  };

  return (
    <div style={containerStyle}>
      <span style={textStyle} title={displayItems.join(', ')}>
        {displayItems.join(', ')}
      </span>
      {hasMore && onViewAll && (
        <Box
          component="button"
          type="button"
          onClick={handleViewAllClick}
          sx={styles.viewAllButton}>
          View all ({totalCount})
        </Box>
      )}
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
};

const textStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emptyStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const styles: Record<string, SxProps<Theme>> = {
  viewAllButton: {
    flexShrink: 0,
    border: 'none',
    backgroundColor: 'transparent',
    font: 'inherit',
    fontSize: '0.75rem',
    color: 'primary.main',
    cursor: 'pointer',
    padding: '2px 4px',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'action.hover',
    },
    '&:active': {
      backgroundColor: 'action.selected',
    },
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: '1px',
    },
  },
};

export default SynonymsCell;
